import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Tooltip, CircularProgress,
  Snackbar, Alert, Chip, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, HealthAndSafety as InsIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const InsuranceMedications: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [medications, setMedications] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ item_id: '', insurance_name: '', coverage_type: 'full', copay_amount: 0, authorization_required: false, max_quantity_per_month: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(getApiUrl('/inventory-module/insurance-medications')); setRecords(res.data.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchMedications = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('/inventory-module/items'), { params: { item_type: 'medicamento', limit: 500 } });
      setMedications(res.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetch(); fetchMedications(); }, [fetch, fetchMedications]);

  const openDialog = (rec?: any) => {
    if (rec) {
      setEditingId(rec.id);
      setForm({ item_id: rec.item_id || '', insurance_name: rec.insurance_name || '', coverage_type: rec.coverage_type || 'full', copay_amount: rec.copay_amount || 0, authorization_required: rec.authorization_required || false, max_quantity_per_month: rec.max_quantity_per_month?.toString() || '', notes: rec.notes || '' });
    } else {
      setEditingId(null);
      setForm({ item_id: '', insurance_name: '', coverage_type: 'full', copay_amount: 0, authorization_required: false, max_quantity_per_month: '', notes: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.item_id || !form.insurance_name.trim()) { setSnackbar({ open: true, message: 'Medicamento e convênio obrigatórios', severity: 'error' }); return; }
    try {
      setSaving(true);
      const payload = { ...form, copay_amount: Number(form.copay_amount), max_quantity_per_month: form.max_quantity_per_month ? parseInt(form.max_quantity_per_month) : null };
      if (editingId) await axios.put(getApiUrl(`/inventory-module/insurance-medications/${editingId}`), payload);
      else await axios.post(getApiUrl('/inventory-module/insurance-medications'), payload);
      setSnackbar({ open: true, message: editingId ? 'Atualizado!' : 'Criado!', severity: 'success' });
      setDialogOpen(false); fetch();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Desativar este registro?')) return;
    try { await axios.delete(getApiUrl(`/inventory-module/insurance-medications/${id}`)); setSnackbar({ open: true, message: 'Desativado!', severity: 'success' }); fetch(); }
    catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Medicamento por Convênio</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar"><IconButton onClick={fetch}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            Novo Vínculo
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Medicamento</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Princípio Ativo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Convênio</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cobertura</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Copagamento</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Autorização</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Qtd Máx/Mês</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#9ca3af' }}>Nenhum registro cadastrado</TableCell></TableRow>
              ) : records.map(rec => (
                <TableRow key={rec.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{rec.clinic_inventory_items?.name || '-'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{rec.clinic_inventory_items?.active_ingredient || '-'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{rec.insurance_name}</Typography></TableCell>
                  <TableCell>
                    <Chip label={rec.coverage_type === 'full' ? 'Total' : rec.coverage_type === 'partial' ? 'Parcial' : 'Sem Cobertura'} size="small"
                      sx={{ bgcolor: rec.coverage_type === 'full' ? '#d1fae5' : rec.coverage_type === 'partial' ? '#fef3c7' : '#fee2e2', color: rec.coverage_type === 'full' ? '#059669' : rec.coverage_type === 'partial' ? '#d97706' : '#dc2626', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(rec.copay_amount)}</TableCell>
                  <TableCell align="center">{rec.authorization_required ? <Chip label="Sim" size="small" color="warning" /> : <Chip label="Não" size="small" />}</TableCell>
                  <TableCell align="center">{rec.max_quantity_per_month || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openDialog(rec)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Desativar"><IconButton size="small" onClick={() => handleDelete(rec.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Vínculo' : 'Novo Vínculo'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small"><InputLabel>Medicamento *</InputLabel>
                <Select value={form.item_id} label="Medicamento *" onChange={(e) => setForm({ ...form, item_id: e.target.value })}>
                  {medications.map(m => <MenuItem key={m.id} value={m.id}>{m.name} {m.concentration ? `(${m.concentration})` : ''}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth required label="Convênio *" value={form.insurance_name} onChange={(e) => setForm({ ...form, insurance_name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Cobertura</InputLabel>
                <Select value={form.coverage_type} label="Cobertura" onChange={(e) => setForm({ ...form, coverage_type: e.target.value })}>
                  <MenuItem value="full">Total</MenuItem>
                  <MenuItem value="partial">Parcial</MenuItem>
                  <MenuItem value="none">Sem Cobertura</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Copagamento (R$)" type="number" value={form.copay_amount} onChange={(e) => setForm({ ...form, copay_amount: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Qtd Máxima/Mês" type="number" value={form.max_quantity_per_month} onChange={(e) => setForm({ ...form, max_quantity_per_month: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={form.authorization_required} onChange={(e) => setForm({ ...form, authorization_required: e.target.checked })} />} label="Requer Autorização Prévia" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Observações" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} size="small" multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InsuranceMedications;
